import React, { useRef, useEffect } from 'react';
import { Linking } from 'react-native';
import { WebView } from 'react-native-webview';
import Toast from 'react-native-toast-message';
import ModalHeader from '@/components/General/ModalHeader';
import { useLocalSearchParams, useRouter } from 'expo-router';

// react-native-webview calls loadFileURL on any source URL without a host —
// mailto:, tel:, sms:, bare domains etc. throw NSInvalidArgumentException and
// crash the app. Only http(s) URLs may reach the WebView. See CHURCH-APP-1CW.
const isWebUrl = (url) => /^https?:\/\//i.test(url || '');

export default function WebViewScreen() {
    const webviewRef = useRef(null);
    const router = useRouter();

    const { url, title, returnURL } = useLocalSearchParams();
    const urlIsWeb = isWebUrl(url);
    const returned = useRef(false);

    useEffect(() => {
        if (!url || urlIsWeb) return;
        // Non-web scheme (mailto:, tel:, sms:) — hand to the OS instead of the
        // WebView, then close this screen.
        Linking.openURL(url).catch(() => {
            Toast.show({ type: 'error', text2: 'Unable to open that link on this device.' });
        });
        if (router.canGoBack()) router.back();
    }, [url, urlIsWeb, router]);

    return (
        <>
            <ModalHeader title={title} cancelText='Close'/>
            {urlIsWeb && (
                <WebView
                    ref={webviewRef}
                    source={{ uri: url }}
                    originWhitelist={['http://*', 'https://*', 'mailto:*', 'tel:*', 'sms:*']}
                    onShouldStartLoadWithRequest={(request) => {
                        if (returnURL && !returned.current) {
                            try {
                                const target = new URL(returnURL);
                                const next = new URL(request.url);
                                if (next.origin === target.origin && next.pathname === target.pathname) {
                                    returned.current = true;
                                    router.back();
                                    return false;
                                }
                            } catch {
                                // Invalid URLs continue through the scheme guard.
                            }
                        }
                        // mailto:/tel:/sms: links inside loaded pages — open
                        // externally instead of erroring inside the WebView.
                        if (request.url && !isWebUrl(request.url)) {
                            Linking.openURL(request.url).catch(() => {
                                Toast.show({ type: 'error', text2: 'Unable to open that link on this device.' });
                            });
                            return false;
                        }
                        return true;
                    }}
                    style={{ flex: 1 }}
                />
            )}
        </>
    );
}
